// Pages inside the portfolio: links to cv.html (data-cv) and to the case
// studies (data-sheet) open that page's <main> in a full-screen panel instead
// of loading another page. The panel shows the page's own content, so each
// page is written once and stays the URL to share. Back closes the panel,
// like leaving a page; /#cv, /#marketplace... open it directly.
import initFit from "./fit";
import initArch from "./arch";

export default function initSheet() {
  // data-cv="fit" (or data-sheet="section") opens the page at that section
  const links = [...document.querySelectorAll("a[data-cv], a[data-sheet]")];
  if (!links.length || typeof HTMLDialogElement === "undefined") return;

  // page file -> its name in the URL hash: cv.html -> #cv
  const pageOf = (link) => link.getAttribute("href").split("#")[0].replace(/^\//, "");
  const hashOf = (page) => page.replace(/\.html$/, "");
  const pages = new Set(links.map(pageOf));

  const sheet = document.createElement("dialog");
  sheet.className = "cv-sheet";
  sheet.innerHTML = `
    <div class="cv-sheet__bar">
      <a class="cv-sheet__page" href="#" target="_blank" rel="noopener">
        <i class="fa fa-external-link" aria-hidden="true"></i> Open as a page
      </a>
      <button type="button" class="cv-sheet__close" aria-label="Close"><i class="fa fa-times" aria-hidden="true"></i></button>
    </div>
    <div class="cv-sheet__body"></div>`;
  document.body.appendChild(sheet);
  const body = sheet.querySelector(".cv-sheet__body");
  const pageLink = sheet.querySelector(".cv-sheet__page");

  // The slide deck listens on window for wheel, swipes and arrow keys: keep
  // them inside the panel so it scrolls and the slides stay put
  for (const type of ["wheel", "touchstart", "touchmove", "touchend", "keydown"]) {
    sheet.addEventListener(type, (e) => e.stopPropagation(), { passive: true });
  }

  const cache = new Map(); // page -> Promise of its <main>
  function load(page) {
    if (!cache.has(page)) {
      cache.set(
        page,
        fetch(page)
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, "text/html");
            const main = doc.querySelector("main");
            if (!main) throw new Error(`no <main> in ${page}`);
            main.querySelector("[data-print]")?.remove(); // would print the home page here
            return { main: document.adoptNode(main), title: doc.title };
          })
          .catch((err) => {
            cache.delete(page); // try again next time
            throw err;
          })
      );
    }
    return cache.get(page);
  }

  let pushed = false;
  let current = "";
  async function open(page, target) {
    let loaded;
    try {
      loaded = await load(page);
    } catch {
      location.href = page + (target ? `#${target}` : ""); // can't load it here: go to the page
      return;
    }
    if (current !== page) {
      body.replaceChildren(loaded.main);
      initFit(body);
      initArch(body);
      current = page;
    }
    sheet.setAttribute("aria-label", loaded.title.split(" | ")[0]);
    pageLink.href = page;
    if (!sheet.open) {
      sheet.showModal();
      history.pushState({ sheet: page }, "", `#${hashOf(page)}`);
      pushed = true;
    } else {
      history.replaceState({ sheet: page }, "", `#${hashOf(page)}`);
    }
    const to = target ? body.querySelector(`#${CSS.escape(target)}`) : null;
    if (to) to.scrollIntoView({ block: "start" });
    else body.scrollTop = 0;
    (to?.querySelector("textarea") || sheet.querySelector(".cv-sheet__close")).focus({ preventScroll: true });
  }

  function close() {
    if (sheet.open) sheet.close();
  }

  for (const link of links) {
    link.addEventListener("click", (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // new tab: the real page
      e.preventDefault();
      open(pageOf(link), link.dataset.cv || link.dataset.sheet || "");
    });
    link.addEventListener("pointerenter", () => load(pageOf(link)).catch(() => {}), { once: true }); // fetch while they aim
  }
  // Jump links inside a page (#mp-features...) scroll the panel: a hash
  // change would close it, or move the slides behind it
  body.addEventListener("click", (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const to = body.querySelector(`#${CSS.escape(a.getAttribute("href").slice(1))}`);
    if (to) {
      e.preventDefault();
      to.scrollIntoView({ block: "start", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    }
  });
  sheet.querySelector(".cv-sheet__close").addEventListener("click", close);
  sheet.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  });
  // Escape, the close button and Back all end here: leave the panel's history entry
  sheet.addEventListener("close", () => {
    if (pushed) {
      pushed = false;
      if (history.state?.sheet) history.back();
    }
  });
  window.addEventListener("popstate", () => {
    if (sheet.open && !history.state?.sheet) {
      pushed = false;
      sheet.close();
    }
  });
  // A shared link to /#cv or /#marketplace opens that page
  const shared = [...pages].find((p) => `#${hashOf(p)}` === location.hash);
  if (shared) {
    history.replaceState(null, "", location.pathname + location.search);
    open(shared, "");
  }
}
